import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Container,
  Box,
  Paper,
  CircularProgress,
  Alert,
  Fade,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  PhoneAndroid,
  Visibility,
  VisibilityOff,
  AdminPanelSettings,
  Lock,
} from '@mui/icons-material';
import { adminLogin } from '../services/auth';
import logo from '../assets/images/logo.png';

const Login = ({ onLogin }) => {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [phoneError, setPhoneError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validatePhone = (phoneNumber) => {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(phoneNumber);
  };

  const handleAdminLogin = async () => {
    setError('');
    setSuccess('');
    
    if (!validatePhone(phone)) {
      setPhoneError(true);
      setError('Please enter a valid 10-digit phone number');
      return;
    }
    
    if (!password) {
      setError('Password is required');
      return;
    }
    
    setPhoneError(false);
    setLoading(true);
    
    try {
      const response = await adminLogin(phone, password);
      console.log('Admin login response:', response);
      setSuccess('Login successful!');
      setTimeout(() => {
        onLogin();
      }, 1000);
    } catch (err) {
      setError(err.message || 'Invalid credentials. Please try again.');
      console.error('Admin login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneChange = (e) => {
    let raw = e.target.value.trim();
    if (raw.startsWith('+91')) raw = raw.slice(3);
    else if (raw.startsWith('+')) raw = raw.slice(1);

    const digits = raw.replace(/\D/g, '');
    let cleanPhone = digits.slice(0, 10);
    if (digits.length >= 12 && digits.startsWith('91')) {
      cleanPhone = digits.slice(2, 12);
    } else if (digits.length === 11 && digits.startsWith('0')) {
      cleanPhone = digits.slice(1, 11);
    }
    setPhone(cleanPhone);
    setPhoneError(false);
    setError('');
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setError('');
  };

  const handleKeyPress = (e, handler) => {
    if (e.key === 'Enter') {
      handler();
    }
  };

  return (
    <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Fade in={true} timeout={800}>
        <Paper
          elevation={6}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #f3f4f6 100%)',
            boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
            border: '1px solid rgba(0,0,0,0.08)'
          }}
        >
          {/* Logo Section */}
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{
              width: 150,
              height: 'auto',
              borderRadius: 2,
              mb: 3,
              objectFit: 'contain'
            }}
          />

          <Typography
            component="h1"
            variant="h4"
            sx={{
              mb: 1,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Admin Portal
          </Typography>

          <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', color: '#6b7280' }}>
            Sign in to access the admin dashboard
          </Typography>

          {/* Error Alert */}
          <Fade in={!!error}>
            <Alert
              severity="error"
              sx={{
                width: '100%',
                mb: 2,
                borderRadius: 2
              }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          </Fade>

          {/* Success Alert */}
          <Fade in={!!success}>
            <Alert
              severity="success"
              sx={{
                width: '100%',
                mb: 2,
                borderRadius: 2
              }}
              onClose={() => setSuccess('')}
            >
              {success}
            </Alert>
          </Fade>

          <TextField
            margin="normal"
            required
            fullWidth
            label="Phone Number"
            placeholder="Enter 10-digit phone number"
            value={phone}
            onChange={handlePhoneChange}
            onKeyPress={(e) => handleKeyPress(e, handleAdminLogin)}
            error={phoneError}
            helperText={phoneError ? 'Please enter a valid 10-digit phone number' : ''}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneAndroid color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#52ad83',
                },
              },
            }}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            label="Password"
            placeholder="Enter your password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={handlePasswordChange}
            onKeyPress={(e) => handleKeyPress(e, handleAdminLogin)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: '#6b7280' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#52ad83',
                },
              },
            }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handleAdminLogin}
            disabled={loading || phone.length !== 10 || !password}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AdminPanelSettings />}
            sx={{
              mt: 3,
              mb: 2,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1.1rem',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #52ad83 0%, #3d8f64 100%)',
              boxShadow: '0 4px 15px rgba(82, 173, 131, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #3d8f64 0%, #52ad83 100%)',
                boxShadow: '0 6px 20px rgba(82, 173, 131, 0.4)',
              },
              '&:disabled': {
                background: '#d1d5db',
                boxShadow: 'none',
                color: '#9ca3af'
              }
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </Paper>
      </Fade>
    </Container>
  );
};

export default Login;
